import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../auth';

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already signed in (e.g. navigated here by hand, or a still-valid session).
  if (user) return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not reach the API');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-lg border border-edge bg-panel p-8"
      >
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">ATLAS</h1>
          <p className="mt-1 text-sm text-slate-400">Sign in to continue.</p>
        </div>

        <label className="block space-y-1.5">
          <span className="text-xs uppercase tracking-wide text-slate-400">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            className="w-full rounded border border-edge bg-surface px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs uppercase tracking-wide text-slate-400">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full rounded border border-edge bg-surface px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
        </label>

        {error && (
          <p role="alert" className="rounded border border-red-900 bg-red-950/60 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded bg-slate-200 px-3 py-2 text-sm font-medium text-slate-900 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
